/**
 * Wire types for GET /api/calendar/range and the single boundary where its
 * ISO-8601 strings become epoch milliseconds for the pure layout core.
 *
 * The response is a discriminated union on `kind` — the three-object taxonomy
 * (commitment / imported event / task block) plus logged time, never merged
 * into a shared "event" shape. `days[]` is the only DST authority: every
 * local-day boundary and wall-clock hour mark is server-computed, and this
 * module converts instants without ever asking the browser a zone question.
 */
import type { CalendarEntryKind, DayBounds, LayoutEntry } from './layoutTypes';

export type CalendarEntryAction =
  | 'respond'
  | 'reschedule'
  | 'keep'
  | 'release'
  | 'move'
  | 'delete'
  | 'edit';

interface ApiEntryBase {
  id: string;
  source_id: number;
  title: string | null;
  start_at: string;
  end_at: string | null;
  all_day: boolean;
  clipped_start: boolean;
  clipped_end: boolean;
  actions: CalendarEntryAction[];
}

export interface ApiCommitmentOccurrenceEntry extends ApiEntryBase {
  kind: 'commitment_occurrence';
  state: string;
  commitment_id: number;
  reschedulable: boolean;
}

export interface ApiPlannedEventEntry extends ApiEntryBase {
  kind: 'planned_event';
  provider: string;
  status: string;
  read_only_reason: string;
}

export interface ApiTaskBlockEntry extends ApiEntryBase {
  kind: 'task_block';
  state: string;
  task_id: number;
  task_title: string | null;
  project_title: string | null;
}

export interface ApiTimeEntryEntry extends ApiEntryBase {
  kind: 'time_entry';
  running: boolean;
  expected_end: string | null;
  source: string;
  description: string | null;
  project: string | null;
  task: string | null;
  overlap_partner_ids: number[];
  overlap_acknowledged: boolean | null;
}

export type ApiCalendarEntry =
  | ApiCommitmentOccurrenceEntry
  | ApiPlannedEventEntry
  | ApiTaskBlockEntry
  | ApiTimeEntryEntry;

export interface ApiHourMark {
  /** 24h clock face, e.g. "14:00" — the server omits a skipped DST hour and
   * repeats an ambiguous one, so this list IS the day's axis. */
  label: string;
  at: string;
}

export interface ApiCalendarDay {
  date: string;
  start_at: string;
  end_at: string;
  minutes: number;
  hour_marks: ApiHourMark[];
  planned_minutes: number;
  logged_minutes: number;
}

export interface ApiHourClassBand {
  hour_class_id: number;
  name: string;
  date: string;
  start_at: string;
  end_at: string;
}

export interface ApiCalendarNotice {
  code: string;
  message: string;
  count: number | null;
}

export interface CalendarRangeResponse {
  timezone: string;
  start_at: string;
  end_at: string;
  days: ApiCalendarDay[];
  entries: ApiCalendarEntry[];
  hour_class_bands: ApiHourClassBand[];
  notices: ApiCalendarNotice[];
}

const parseMs = (iso: string): number => Date.parse(iso);

/** A server day row with its layout bounds plus the display-only extras. */
export interface CalendarDayView {
  bounds: DayBounds;
  hourMarks: { label: string; atMs: number }[];
  plannedMinutes: number;
  loggedMinutes: number;
}

export const toDayView = (day: ApiCalendarDay): CalendarDayView => ({
  bounds: {
    dateKey: day.date,
    startMs: parseMs(day.start_at),
    endMs: parseMs(day.end_at),
    hours: day.minutes / 60,
  },
  hourMarks: day.hour_marks.map((mark) => ({
    label: mark.label,
    atMs: parseMs(mark.at),
  })),
  plannedMinutes: day.planned_minutes,
  loggedMinutes: day.logged_minutes,
});

export const toLayoutEntry = (entry: ApiCalendarEntry): LayoutEntry => ({
  id: entry.id,
  kind: entry.kind as CalendarEntryKind,
  startMs: parseMs(entry.start_at),
  // A null end means "running" ONLY for logged time (layoutTypes contract).
  // An open-ended imported event is a fact with no duration: it becomes a
  // zero-duration marker here, and the layout core guards the same rule.
  endMs:
    entry.end_at === null
      ? entry.kind === 'time_entry'
        ? null
        : parseMs(entry.start_at)
      : parseMs(entry.end_at),
  allDay: entry.all_day,
  clippedStart: entry.clipped_start,
  clippedEnd: entry.clipped_end,
  running: entry.kind === 'time_entry' ? entry.running : undefined,
  expectedEndMs:
    entry.kind === 'time_entry' && entry.expected_end !== null
      ? parseMs(entry.expected_end)
      : null,
});
